import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CableIcon from '@mui/icons-material/Cable';
import { useConnectorTypes, useCreateConnection } from 'api/connectors';
import { extractApiError, hasFieldErrors, normalizeFieldErrors } from 'api/errors';
import { takeOAuthDraft } from 'api/oauth';
import JsonSchemaForm from 'components/common/JsonSchemaForm';
import OAuthConnectField from 'components/common/OAuthConnectField';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';
import { localizeConnectorType, translateMetadata } from 'i18n/metadata';

const CATEGORY_COLOR = {
  database:  'primary',
  saas:      'secondary',
  file:      'default',
  streaming: 'warning',
};

const STEPS = () => ([t('data.chooseType'), t('data.configure')]);

export default function NewConnectionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: types, isLoading: typesLoading } = useConnectorTypes();
  const createConnection = useCreateConnection();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [name, setName] = useState('');
  const [config, setConfig] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  // The connected account (OAuth grant) standing in for the type's token field.
  const [grantId, setGrantId] = useState(null);
  const oauthErrorCode = searchParams.get('oauth_error');

  // ── Deep link: ?type=<connector_type_slug> preselects the type and jumps to
  // the Configure step. Applied once so Back doesn't re-advance; unknown
  // slugs are ignored.
  const typeParam = searchParams.get('type');
  const appliedTypeParam = useRef(false);
  useEffect(() => {
    if (appliedTypeParam.current || !typeParam || !types) return;
    appliedTypeParam.current = true;
    if (typeParam === 'bigquery') {
      // BigQuery has its own wizard too: the flat form cannot hold a column
      // selection, so without it every stream would be SELECT *.
      navigate('/console/connectors/new/bigquery', { replace: true });
      return;
    }
    if (typeParam === 'yandex-appmetrica') {
      // AppMetrica: the flat form cannot hold a per-table field selection.
      navigate('/console/connectors/new/appmetrica', { replace: true });
      return;
    }
    if (typeParam === 'yandex-direct') {
      // Direct: an agency's client list and the stream groups need the wizard.
      navigate('/console/connectors/new/direct', { replace: true });
      return;
    }
    if (typeParam === 's3-files') {
      // File sources have their own multi-step wizard (bucket → sources →
      // schedules) — the generic schema form can't express the tables array.
      navigate('/console/connectors/new/files', { replace: true });
      return;
    }
    const match = types.find((t) => t.slug === typeParam);
    if (!match) return;
    setSelectedType(match);
    // Back from the consent screen: the callback appends ?oauth_grant= (or
    // ?oauth_error=) and the form draft was parked in sessionStorage.
    const draft = match.auth?.kind === 'oauth' ? takeOAuthDraft(match.auth.provider) : null;
    setName(draft?.name || '');
    setConfig(draft?.config || {});
    setGrantId(searchParams.get('oauth_grant') || null);
    setFormErrors({});
    setActiveStep(1);
  }, [typeParam, types, navigate, searchParams]);

  // With an account connected, the token field and the picker-filled resource
  // field leave the schema form; without one the plain form stays.
  const auth = selectedType?.auth?.kind === 'oauth' ? selectedType.auth : null;
  const hiddenKeys = new Set();
  if (auth && grantId) {
    hiddenKeys.add(auth.config_key);
    if (auth.resource_kind && auth.resource_field) hiddenKeys.add(auth.resource_field);
  }
  const baseSchema = localizeConnectorType(selectedType)?.config_schema || {};
  const formSchema = hiddenKeys.size
    ? {
        ...baseSchema,
        properties: Object.fromEntries(
          Object.entries(baseSchema.properties || {}).filter(([key]) => !hiddenKeys.has(key))
        ),
        required: (baseSchema.required || []).filter((key) => !hiddenKeys.has(key)),
      }
    : baseSchema;

  // ── Step 1: category filter ───────────────────────────────────────────────
  const [categoryFilter, setCategoryFilter] = useState('all');
  const categories = types
    ? ['all', ...new Set(types.map((t) => t.category))]
    : ['all'];
  const filteredTypes = types
    ? types.filter((t) => categoryFilter === 'all' || t.category === categoryFilter)
    : [];

  // ── Step 1 → Step 2 ───────────────────────────────────────────────────────
  const handleSelectType = (type) => {
    setSelectedType(type);
    setConfig({});
    setGrantId(null);
    setFormErrors({});
  };

  const handleNext = () => {
    if (!selectedType) return;
    if (selectedType.slug === 'bigquery') {
      navigate('/console/connectors/new/bigquery');
      return;
    }
    if (selectedType.slug === 'yandex-appmetrica') {
      navigate('/console/connectors/new/appmetrica');
      return;
    }
    if (selectedType.slug === 'yandex-direct') {
      navigate('/console/connectors/new/direct');
      return;
    }
    if (selectedType.slug === 's3-files') {
      // Dedicated wizard, as in the deep-link branch above.
      navigate('/console/connectors/new/files');
      return;
    }
    setActiveStep(1);
  };

  // ── Step 2: submit ────────────────────────────────────────────────────────
  const validateForm = () => {
    const required = formSchema.required || [];
    const errors = {};
    required.forEach((key) => {
      if (!config[key] && config[key] !== 0 && config[key] !== false) {
        errors[key] = t('data.thisFieldIsRequired');
      }
    });
    // Said under the field: the server refuses it too, but as an error on the
    // whole config, which no field would show.
    if (auth && !grantId && auth.config_key && !config[auth.config_key]) {
      errors[auth.config_key] = t('data.connectAnAccountAboveOrEnterThisByHand');
    }
    return errors;
  };

  // Errors keyed by something the form has no field for go to the submit error.
  const applyServerErrors = (data) => {
    const fieldErrors = normalizeFieldErrors(data);
    if (fieldErrors.name) fieldErrors._name = fieldErrors.name;
    const known = new Set([...Object.keys(formSchema.properties || {}), 'name', '_name']);
    const stray = Object.entries(fieldErrors).filter(([key]) => !known.has(key));
    if (stray.length > 0) setSubmitError(stray.map(([, message]) => message).join(' '));
    setFormErrors(fieldErrors);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setFormErrors({ _name: t('data.connectionNameIsRequired') });
      return;
    }
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    // The hidden resource field is still required: say where to look.
    if (auth && grantId && auth.resource_field && !config[auth.resource_field]) {
      setSubmitError(t('data.pickAValueFromTheConnectedAccountAbove', { value1: translateMetadata(auth.resource_kind) }));
      return;
    }
    setFormErrors({});
    setSubmitError(null);
    // A token typed before connecting would only be dropped server-side.
    const payloadConfig =
      auth && grantId
        ? Object.fromEntries(Object.entries(config).filter(([key]) => key !== auth.config_key))
        : config;
    try {
      const conn = await createConnection.mutateAsync({
        name: name.trim(),
        connector_type_slug: selectedType.slug,
        config: payloadConfig,
        ...(grantId ? { oauth_grant_id: grantId } : {}),
      });
      navigate(`/console/connectors/${conn.public_id}`);
    } catch (err) {
      if (hasFieldErrors(err)) {
        applyServerErrors(err.response.data);
      } else {
        setSubmitError(extractApiError(err, t('data.failedToCreateConnectionPleaseTryAgain')));
      }
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/console/connectors')}
          size="small"
          color="inherit"
        >{t('data.back')}</Button>
        <Divider orientation="vertical" flexItem />
        <CableIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>{t('data.newConnection')}</Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4, maxWidth: 480 }}>
        {STEPS().map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ── Step 1: Choose type ───────────────────────────────────────────── */}
      {activeStep === 0 && (
        <Box>
          {/* Category pills */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat === 'all' ? t('data.all') : translateMetadata(cat)}
                onClick={() => setCategoryFilter(cat)}
                color={categoryFilter === cat ? 'primary' : 'default'}
                variant={categoryFilter === cat ? 'filled' : 'outlined'}
                sx={{ textTransform: 'capitalize' }}
              />
            ))}
          </Box>

          {typesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 2,
                mb: 4,
              }}
            >
              {filteredTypes.map((type) => {
                const isSelected = selectedType?.slug === type.slug;
                return (
                  <Card
                    key={type.slug}
                    elevation={0}
                    sx={(theme) => ({
                      border: '1px solid',
                      borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
                      borderRadius: 2,
                      transition: 'border-color 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                    })}
                  >
                    <CardActionArea
                      onClick={() => handleSelectType(type)}
                      sx={(theme) => ({
                        p: 2,
                        flex: 1,
                        alignItems: 'flex-start',
                        backgroundColor: isSelected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                        transition: 'background-color 0.15s',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? alpha(theme.palette.primary.main, 0.14)
                            : alpha(theme.palette.primary.main, 0.05),
                        },
                        '& .MuiCardActionArea-focusHighlight': { background: 'transparent' },
                      })}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          {localizeConnectorType(type).name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          <Chip
                            label={translateMetadata(type.category)}
                            size="small"
                            color={CATEGORY_COLOR[type.category] || 'default'}
                            sx={{ textTransform: 'capitalize', boxShadow: 'none' }}
                          />
                          {(type.can_be_target || type.meltano_target) && (
                            <Chip
                              label={t('data.target')}
                              size="small"
                              variant="outlined"
                              color="success"
                            />
                          )}
                        </Box>
                        {type.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{
                              mt: 0.5,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {localizeConnectorType(type).description}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={!selectedType}
            >{t('data.nextConfigure')}</Button>
          </Box>
        </Box>
      )}

      {/* ── Step 2: Configure ─────────────────────────────────────────────── */}
      {activeStep === 1 && selectedType && (
        <Box sx={{ maxWidth: 560 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {localizeConnectorType(selectedType).name}
            </Typography>
            <Chip
              label={translateMetadata(selectedType.category)}
              size="small"
              color={CATEGORY_COLOR[selectedType.category] || 'default'}
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {/* Connection name */}
          <TextField
            label={t('data.connectionName2')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={!!formErrors._name}
            helperText={formErrors._name || t('data.aUniqueNameToIdentifyThisConnection')}
          />

          <Divider sx={{ mb: 2 }} />

          {auth && (
            <>
              <OAuthConnectField
                auth={auth}
                connectorTypeSlug={selectedType.slug}
                grantId={grantId}
                onGrantChange={setGrantId}
                resourceValue={auth.resource_field ? config[auth.resource_field] : undefined}
                onResourceChange={(value) => setConfig({ ...config, [auth.resource_field]: value })}
                returnTo={`/console/connectors/new?type=${selectedType.slug}`}
                draft={{ name, config }}
                errorCode={oauthErrorCode}
              />
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Dynamic config form */}
          <JsonSchemaForm
            schema={formSchema}
            value={config}
            onChange={setConfig}
            errors={formErrors}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveStep(0)}
              disabled={createConnection.isPending}
            >{t('data.back')}</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={createConnection.isPending}
              startIcon={createConnection.isPending ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {createConnection.isPending ? t('data.creating') : t('data.createConnection')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
